import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, Wallet } from "lucide-react";
import { toast } from "sonner";
import { documentAPI } from "@/lib/api";
import { DateField } from "@/components/ui/date-field";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { usePaymentMethods } from "@/lib/paymentMethods";

const formatEur = (v) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(v || 0);

export function AcompteDialog({ open, onOpenChange, devisId, onCreated }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("pct"); // "pct" | "amount"
  const [pct, setPct] = useState("30");
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [chosenMethod, setPaymentMethod] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const pm = usePaymentMethods();
  const paymentMethod = chosenMethod || pm.defaultValue;

  useEffect(() => {
    if (!open || !devisId) return;
    setLoading(true);
    documentAPI.acompteSummary(devisId)
      .then((r) => setSummary(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || "Erreur"))
      .finally(() => setLoading(false));
    setMode("pct"); setPct("30"); setAmount("");
    setDate(new Date().toISOString().split("T")[0]);
    setPaymentMethod("");
  }, [open, devisId]);

  const remaining = summary?.remaining_ttc || 0;
  const computedAmount = mode === "pct"
    ? Math.round((summary?.total_ttc || 0) * (parseFloat(pct) || 0)) / 100
    : (parseFloat(amount) || 0);
  const computedPct = summary?.total_ttc > 0 ? (computedAmount * 100 / summary.total_ttc) : 0;

  const valid = computedAmount > 0 && computedAmount <= remaining + 0.01;

  const submit = async () => {
    if (!valid) return;
    setSubmitting(true);
    try {
      const payload = mode === "pct"
        ? { pct: parseFloat(pct), date, payment_method: paymentMethod }
        : { amount_ttc: parseFloat(amount), date, payment_method: paymentMethod };
      const res = await documentAPI.createAcompte(devisId, payload);
      toast.success(`Facture d'acompte ${res.data.number} créée`);
      onOpenChange(false);
      onCreated?.(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="acompte-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Wallet size={18} className="text-indigo-600" />
            Créer une facture d'acompte
            {summary?.devis_number && <span className="text-slate-400 font-normal text-sm">— sur devis {summary.devis_number}</span>}
          </DialogTitle>
        </DialogHeader>
        {loading || !summary ? (
          <div className="py-8 flex items-center justify-center text-slate-400 text-sm">
            <Loader2 size={16} className="animate-spin mr-2" /> Chargement…
          </div>
        ) : (
          <div className="space-y-3 text-sm">
            <div className="rounded-md border border-slate-200 bg-slate-50/60 p-3 grid grid-cols-3 gap-2">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-500">Total devis TTC</p>
                <p className="font-bold text-slate-800 money-value" data-testid="acompte-total">{formatEur(summary.total_ttc)}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-500">Déjà acompté</p>
                <p className="font-bold text-slate-800 money-value" data-testid="acompte-already">{formatEur(summary.sum_acomptes_ttc)}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-500">Reste à facturer</p>
                <p className="font-bold text-emerald-700 money-value" data-testid="acompte-remaining">{formatEur(summary.remaining_ttc)}</p>
              </div>
            </div>
            {summary.deposits?.length > 0 && (
              <div className="rounded-md border border-slate-200 p-2 text-xs">
                <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Acomptes déjà émis</p>
                <ul className="space-y-0.5">
                  {summary.deposits.map((d) => (
                    <li key={d.invoice_id} className="flex justify-between" data-testid={`acompte-prev-${d.invoice_number}`}>
                      <span className="text-slate-700">{d.invoice_number} <span className="text-slate-400">— {d.date}</span></span>
                      <span className="font-medium text-slate-800">{formatEur(d.amount_ttc)} <span className="text-slate-400 font-normal">({d.pct?.toFixed(0)} %)</span></span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div>
              <div className="inline-flex rounded-md border border-slate-200 p-0.5 bg-slate-50">
                <button type="button" onClick={() => setMode("pct")} className={`px-3 py-1 text-xs rounded ${mode === "pct" ? "bg-indigo-600 text-white" : "text-slate-600"}`} data-testid="acompte-mode-pct">Pourcentage</button>
                <button type="button" onClick={() => setMode("amount")} className={`px-3 py-1 text-xs rounded ${mode === "amount" ? "bg-indigo-600 text-white" : "text-slate-600"}`} data-testid="acompte-mode-amount">Montant TTC</button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 block mb-1">
                  {mode === "pct" ? "Pourcentage du total (%)" : "Montant TTC (€)"}
                </label>
                {mode === "pct" ? (
                  <input type="number" min="0" max="100" step="0.01" value={pct} onChange={(e) => setPct(e.target.value)}
                    className="w-full h-9 px-3 border border-slate-300 rounded-md focus:border-indigo-500 outline-none" data-testid="acompte-pct-input" />
                ) : (
                  <input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
                    className="w-full h-9 px-3 border border-slate-300 rounded-md focus:border-indigo-500 outline-none" data-testid="acompte-amount-input" />
                )}
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 block mb-1">Date</label>
                <DateField value={date} onChange={(e) => setDate(e.target.value)}
                  className="w-full h-9 px-3 border border-slate-300 rounded-md focus:border-indigo-500 outline-none" data-testid="acompte-date-input" />
              </div>
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 block mb-1">Mode de règlement</label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger className="h-9 text-sm" data-testid="acompte-payment-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {pm.options.map((m) => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="rounded-md border border-indigo-200 bg-indigo-50/50 p-3 flex justify-between items-center" data-testid="acompte-preview">
              <span className="text-xs text-indigo-700">Acompte à facturer :</span>
              <div className="text-right">
                <p className="text-base font-bold text-indigo-900 money-value">{formatEur(computedAmount)}</p>
                <p className="text-[10px] text-indigo-600">{computedPct.toFixed(1)} % du total devis</p>
              </div>
            </div>

            {!valid && computedAmount > 0 && (
              <p className="text-xs text-red-600" data-testid="acompte-error">Le montant dépasse le solde restant ({formatEur(remaining)})</p>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)} data-testid="acompte-cancel-btn">Annuler</Button>
          <Button size="sm" disabled={!valid || submitting} onClick={submit}
            className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="acompte-submit-btn">
            {submitting && <Loader2 size={14} className="mr-1.5 animate-spin" />}
            Créer la facture d'acompte
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
